/**
 * SyncedBlock — Shared content block backed by documents.synced_blocks.
 * Editing any instance updates all other instances referencing the same syncId.
 */

import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import { createReactBlockSpec } from '@blocknote/react';
import { Link2, Unlink, RefreshCw, Loader2 } from 'lucide-react';

const NestedEditor = lazy(() => import('./NestedEditor'));

const SYNCED_API = '/api/org/documents/synced-blocks';

// ── API helpers ───────────────────────────────────────────────────────────

async function fetchSyncedBlock(syncId: string): Promise<{ content: unknown } | null> {
  try {
    const res = await fetch(`${SYNCED_API}/${syncId}`, { credentials: 'include' });
    if (!res.ok) return null;
    return res.json();
  } catch {
    return null;
  }
}

async function updateSyncedContent(syncId: string, content: unknown): Promise<void> {
  await fetch(`${SYNCED_API}/${syncId}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify({ content }),
  });
}

async function createSyncedBlock(content: unknown): Promise<string | null> {
  try {
    const res = await fetch(SYNCED_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ content }),
    });
    if (!res.ok) return null;
    const data = await res.json();
    return data.id;
  } catch {
    return null;
  }
}

// ── Renderer ──────────────────────────────────────────────────────────────

interface SyncedBlockRendererProps {
  block: {
    id: string;
    props: { syncId: string };
  };
  editor: {
    updateBlock: (id: string, update: { props: Partial<{ syncId: string }> }) => void;
    replaceBlocks: (ids: string[], blocks: unknown[]) => void;
    isEditable: boolean;
  };
}

function SyncedBlockRenderer({ block, editor }: SyncedBlockRendererProps) {
  const { syncId } = block.props;
  const isEditable = editor.isEditable;
  const [content, setContent] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  // Fetch synced content on mount
  useEffect(() => {
    if (!syncId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      const data = await fetchSyncedBlock(syncId);
      if (cancelled) return;
      if (data) {
        setContent(typeof data.content === 'string' ? data.content : JSON.stringify(data.content));
        setError(false);
      } else {
        setError(true);
      }
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [syncId]);

  const handleChange = useCallback(
    (newContent: string) => {
      setContent(newContent);
      // Debounced save to API
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        if (!syncId) return;
        try {
          const parsed = JSON.parse(newContent);
          await updateSyncedContent(syncId, parsed);
        } catch { /* ignore parse errors */ }
      }, 800);
    },
    [syncId]
  );

  // Cleanup debounce
  useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  // No sync ID yet — offer to create one
  if (!syncId) {
    return (
      <div
        className="synced-block-empty flex items-center gap-2 px-4 py-3 rounded-lg border-2 border-dashed border-purple-200 bg-purple-50/50 my-1"
        contentEditable={false}
      >
        <Link2 className="w-4 h-4 text-purple-400" />
        <span className="text-sm text-purple-600">Synced Block</span>
        {isEditable && (
          <button
            onClick={async () => {
              const newId = await createSyncedBlock([]);
              if (newId) {
                editor.updateBlock(block.id, { props: { syncId: newId } });
              }
            }}
            className="ml-auto px-3 py-1 text-xs font-medium bg-purple-600 text-white rounded-md
                       hover:bg-purple-700 transition-colors"
          >
            Create synced block
          </button>
        )}
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 px-4 py-4 text-sm text-stone-400" contentEditable={false}>
        <Loader2 className="w-4 h-4 animate-spin" /> Loading synced content...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 px-4 py-3 rounded-lg border border-red-200 bg-red-50 text-sm text-red-600 my-1" contentEditable={false}>
        <RefreshCw className="w-4 h-4" />
        Synced block not found (ID: {syncId.slice(0, 8)}...)
      </div>
    );
  }

  return (
    <div
      className="synced-block my-2 rounded-lg border-2 border-dashed border-purple-200 relative"
      contentEditable={false}
    >
      {/* Header badge */}
      <div className="flex items-center gap-2 px-3 py-1 bg-purple-50/50 text-xs text-purple-500 border-b border-purple-100">
        <Link2 className="w-3 h-3" />
        <span className="font-medium">Synced</span>
        <span className="text-purple-300 truncate">{syncId.slice(0, 8)}</span>
        {isEditable && (
          <button
            onClick={() => {
              // Detach: keep the current content as regular blocks but remove sync link
              editor.updateBlock(block.id, { props: { syncId: '' } });
            }}
            className="ml-auto flex items-center gap-1 text-purple-400 hover:text-purple-600 transition-colors"
            title="Detach from synced source"
          >
            <Unlink className="w-3 h-3" /> Detach
          </button>
        )}
      </div>

      {/* Content editor */}
      <div className="p-2">
        <Suspense fallback={<div className="text-xs text-stone-400 py-2">Loading editor...</div>}>
          <NestedEditor
            initialContent={content}
            onChange={handleChange}
            editable={isEditable}
            placeholder="Synced content..."
            debounceMs={500}
          />
        </Suspense>
      </div>
    </div>
  );
}

// ── Block spec ────────────────────────────────────────────────────────────

export const syncedBlockSpec = createReactBlockSpec(
  {
    type: 'syncedBlock' as const,
    propSchema: {
      syncId: { default: '' },
    },
    content: 'none',
  },
  {
    render: (props) => {
      const blockProps: SyncedBlockRendererProps = {
        block: {
          id: props.block.id,
          props: props.block.props as { syncId: string },
        },
        editor: {
          updateBlock: (id, update) => (props.editor as any).updateBlock(id, update),
          replaceBlocks: (ids, blocks) => (props.editor as any).replaceBlocks(ids, blocks),
          isEditable: (props.editor as any).isEditable,
        },
      };
      return <SyncedBlockRenderer {...blockProps} />;
    },
  }
);

export default syncedBlockSpec;
